// Bedrock Claude Global 推論プロファイル ID（AWS モデルカード記載の正式 ID）
// 5.5 系は日付・`-v1:0` を持たない新形式
const BEDROCK_GLOBAL_SONNET_5_5_ID = 'global.anthropic.claude-sonnet-5-5';
const BEDROCK_GLOBAL_HAIKU_5_5_ID = 'global.anthropic.claude-haiku-5-5';
const BEDROCK_GLOBAL_OPUS_5_5_ID = 'global.anthropic.claude-opus-5-5';
// Anthropic 以外の Bedrock モデル（Global 推論プロファイル。系統エイリアス sol/luna/grok/kimi の解決先）
const BEDROCK_GLOBAL_GPT_6_1_SOL_ID = 'global.openai.gpt-6.1-sol';
const BEDROCK_GLOBAL_GPT_6_LUNA_ID = 'global.openai.gpt-6-luna';
const BEDROCK_GLOBAL_GROK_4_7_ID = 'global.xai.grok-4.7';
const BEDROCK_GLOBAL_KIMI_K3_ID = 'global.moonshotai.kimi-k3';
// GPT-6 系の長文脈料金の閾値（入力がこれを超えるとリクエスト全体に長文脈単価を適用）
const GPT_6_LONG_CONTEXT_THRESHOLD = 272_000;
// 推論強度は Converse の additionalModelRequestFields.reasoning_config で指定する。
// levels は受け付ける推論レベル（off は none を送って推論を止める）。
// 受け付ける値はモデルごとに異なる（2026-10 実機確認）:
//   GPT-6.1 Sol: low〜max（none 非対応） / GPT-6 Luna・Kimi K3: none〜max / Grok 4.7: low〜xhigh
// GPT-6 系は Converse の cachePoint による明示キャッシュ非対応（暗黙キャッシュのみ）。
// GPT-6 系・Grok 4.7・Kimi K3 は Converse で PDF などの文書ブロックを
// 「This model doesn't support documents.」で拒否する。また、ツール結果の中の画像を
// 「doesn't support the image field for user messages」で拒否する（ツール結果の外に置けば読める。実機確認）
// GPT-6.1 Sol: none を受け付けないため推論を止められない（オフは low へ丸める）
const GPT_6_1_SOL_CAPABILITIES = {
    reasoning: {
        kind: 'effort',
        effortField: 'reasoning_config',
        levels: ['low', 'medium', 'high', 'xhigh', 'max'],
    },
    supportsExplicitPromptCache: false,
    maxOutputTokens: 128_000,
    supportsDocumentInput: false,
    acceptsPriorReasoning: true,
    supportsToolResultImage: false,
};
// GPT-6 Luna: none で推論を止められる
const GPT_6_LUNA_CAPABILITIES = {
    ...GPT_6_1_SOL_CAPABILITIES,
    reasoning: {
        kind: 'effort',
        effortField: 'reasoning_config',
        levels: ['off', 'low', 'medium', 'high', 'xhigh', 'max'],
    },
};
// Claude 5.5 系: adaptive thinking 必須（budget_tokens 指定は 400）、最大出力 128K
const CLAUDE_ADAPTIVE_BASE_CAPABILITIES = {
    supportsExplicitPromptCache: true,
    maxOutputTokens: 128_000,
    supportsDocumentInput: true,
    acceptsPriorReasoning: true,
    supportsToolResultImage: true,
};
// Opus 5.5: 推論を止められない（thinking.type の disabled / between_tools とも 400）
const CLAUDE_OPUS_5_5_CAPABILITIES = {
    ...CLAUDE_ADAPTIVE_BASE_CAPABILITIES,
    reasoning: { kind: 'adaptive', levels: ['low', 'medium', 'high', 'xhigh', 'max'] },
};
// Sonnet 5.5: thinking.type=between_tools で推論を止められる（disabled は 400）
const CLAUDE_SONNET_5_5_CAPABILITIES = {
    ...CLAUDE_ADAPTIVE_BASE_CAPABILITIES,
    reasoning: {
        kind: 'adaptive',
        levels: ['off', 'low', 'medium', 'high', 'xhigh', 'max'],
        offThinkingType: 'between_tools',
    },
};
// Haiku 5.5: thinking.type=disabled で推論を止められる（between_tools は 400）
const CLAUDE_HAIKU_5_5_CAPABILITIES = {
    ...CLAUDE_ADAPTIVE_BASE_CAPABILITIES,
    reasoning: {
        kind: 'adaptive',
        levels: ['off', 'low', 'medium', 'high', 'xhigh', 'max'],
        offThinkingType: 'disabled',
    },
};
// モデルレジストリ
export const MODEL_REGISTRY = {
    bedrock: [
        // Global Cross-Region Inference Profile（AWS 公表: regional プロファイルから約 10% ディスカウント）
        {
            id: BEDROCK_GLOBAL_OPUS_5_5_ID,
            displayName: 'Claude Opus 5.5',
            provider: 'bedrock',
            supportsReasoning: true,
            supportsTools: true,
            supportsVision: true,
            contextWindow: 1_000_000,
            // Opus 5.5 のトークン単価（Global は公表価格そのもの。Regional / Geo が 10% 増し）
            pricing: {
                inputTokens: 4.0,
                outputTokens: 20.0,
                cachedTokens: 0.4,
                cacheWriteTokens: 5.0,
                reasoningTokens: 20.0,
            },
            capabilities: CLAUDE_OPUS_5_5_CAPABILITIES,
        },
        {
            id: BEDROCK_GLOBAL_SONNET_5_5_ID,
            displayName: 'Claude Sonnet 5.5',
            provider: 'bedrock',
            supportsReasoning: true,
            supportsTools: true,
            supportsVision: true,
            contextWindow: 1_000_000,
            // Sonnet 5.5 のトークン単価（Anthropic 公表: $2/$10 per MTok）
            pricing: {
                inputTokens: 2.0,
                outputTokens: 10.0,
                cachedTokens: 0.2,
                cacheWriteTokens: 2.5,
                reasoningTokens: 10.0,
            },
            capabilities: CLAUDE_SONNET_5_5_CAPABILITIES,
        },
        {
            id: BEDROCK_GLOBAL_HAIKU_5_5_ID,
            displayName: 'Claude Haiku 5.5',
            provider: 'bedrock',
            supportsReasoning: true,
            supportsTools: true,
            supportsVision: true,
            contextWindow: 1_000_000,
            // Haiku 5.5 のトークン単価（Anthropic 公表: 入力 100K 以下で $0.10/$0.50、超えると $0.50/$2.50 per MTok）
            pricing: {
                inputTokens: 0.1,
                outputTokens: 0.5,
                cachedTokens: 0.01,
                cacheWriteTokens: 0.125,
                reasoningTokens: 0.5,
                longContext: {
                    thresholdInputTokens: 100_000,
                    inputTokens: 0.5,
                    outputTokens: 2.5,
                    cachedTokens: 0.05,
                    cacheWriteTokens: 0.625,
                    reasoningTokens: 2.5,
                },
            },
            capabilities: CLAUDE_HAIKU_5_5_CAPABILITIES,
        },
        // 以下は Global CRIS の Standard 単価（AWS モデルカード記載）
        {
            id: BEDROCK_GLOBAL_GPT_6_1_SOL_ID,
            displayName: 'GPT-6.1 Sol',
            provider: 'bedrock',
            supportsReasoning: true,
            supportsTools: true,
            supportsVision: true,
            contextWindow: 1_000_000,
            pricing: {
                inputTokens: 2.0,
                outputTokens: 10.0,
                cachedTokens: 0.1,
                cacheWriteTokens: 2.5,
                reasoningTokens: 10.0,
                longContext: {
                    thresholdInputTokens: GPT_6_LONG_CONTEXT_THRESHOLD,
                    inputTokens: 4.0,
                    outputTokens: 15.0,
                    cachedTokens: 0.2,
                    cacheWriteTokens: 5.0,
                    reasoningTokens: 15.0,
                },
            },
            capabilities: GPT_6_1_SOL_CAPABILITIES,
        },
        {
            id: BEDROCK_GLOBAL_GPT_6_LUNA_ID,
            displayName: 'GPT-6 Luna',
            provider: 'bedrock',
            supportsReasoning: true,
            supportsTools: true,
            supportsVision: true,
            contextWindow: 1_050_000,
            pricing: {
                inputTokens: 0.1,
                outputTokens: 0.5,
                cachedTokens: 0.01,
                cacheWriteTokens: 0.125,
                reasoningTokens: 0.5,
                longContext: {
                    thresholdInputTokens: GPT_6_LONG_CONTEXT_THRESHOLD,
                    inputTokens: 0.2,
                    outputTokens: 0.75,
                    cachedTokens: 0.02,
                    cacheWriteTokens: 0.25,
                    reasoningTokens: 0.75,
                },
            },
            capabilities: GPT_6_LUNA_CAPABILITIES,
        },
        {
            id: BEDROCK_GLOBAL_GROK_4_7_ID,
            displayName: 'Grok 4.7',
            provider: 'bedrock',
            supportsReasoning: true,
            supportsTools: true,
            supportsVision: true,
            contextWindow: 500_000,
            // キャッシュ書込単価は非公表（暗黙キャッシュのみ）のため、過小計上を避けて入力単価と同値にする
            pricing: {
                inputTokens: 2.0,
                outputTokens: 6.0,
                cachedTokens: 0.5,
                cacheWriteTokens: 2.0,
                reasoningTokens: 6.0,
            },
            // 推論は常時有効で無効化できない（none と max は非対応、既定 high）。オフは low へ丸める。
            // 最大出力は 500000 まで受け付ける（実機確認）が、実用上 128000 に揃える
            capabilities: {
                reasoning: {
                    kind: 'effort',
                    effortField: 'reasoning_config',
                    levels: ['low', 'medium', 'high', 'xhigh'],
                },
                supportsExplicitPromptCache: false,
                maxOutputTokens: 128_000,
                supportsDocumentInput: false,
                acceptsPriorReasoning: true,
                supportsToolResultImage: false,
            },
        },
        {
            id: BEDROCK_GLOBAL_KIMI_K3_ID,
            displayName: 'Kimi K3',
            provider: 'bedrock',
            supportsReasoning: true,
            supportsTools: true,
            supportsVision: true,
            contextWindow: 1_000_000,
            pricing: {
                inputTokens: 3.0,
                outputTokens: 15.0,
                cachedTokens: 0.3,
                cacheWriteTokens: 3.75,
                reasoningTokens: 15.0,
            },
            // 推論強度は reasoning_config で none〜max を指定でき、none で推論ブロックを出さない（実機確認）。
            // Converse では過去ターンの推論内容を含むと InternalServerException、PDF / HTML の文書入力は拒否される。
            // 最大出力の上限は 128000（それを超えると ValidationException）
            capabilities: {
                reasoning: {
                    kind: 'effort',
                    effortField: 'reasoning_config',
                    levels: ['off', 'low', 'medium', 'high', 'xhigh', 'max'],
                },
                supportsExplicitPromptCache: false,
                maxOutputTokens: 128_000,
                supportsDocumentInput: false,
                acceptsPriorReasoning: false,
                supportsToolResultImage: false,
            },
        },
    ],
};
// デフォルトモデル。初回起動時のチャットの選択と、未知・非対応 ID のフォールバック先を兼ねる。
// 利用料の上限に達しにくいよう、低価格の Claude Haiku 5.5 を既定にする
const DEFAULT_MODELS = {
    bedrock: BEDROCK_GLOBAL_HAIKU_5_5_ID,
};
/**
 * Legacy Bedrock ID → 現行 Global Bedrock ID の静的マッピング辞書
 *
 * 既存ユーザーのワークスペース設定・タスク履歴・スケジュールに保存済みの旧 ID を、
 * 実行時に現行 Global ID へ正規化するために使用する（永続化値は書き換えない）。
 *
 * 対象:
 * - JP 推論プロファイル（`jp.anthropic.*`）: Global 移行前の旧 ID
 * - レジストリから除外した旧世代モデル（Opus 4.7 / 4.8 / 5 / Sonnet 4.6 / 5 / Haiku 4.5）: 保存済みの
 *   具体 ID を同ティアの現行モデル（Opus 5.5 / Sonnet 5.5 / Haiku 5.5）へ読み替え、
 *   既定モデル（Claude Haiku 5.5）への意図しないフォールバックを防ぐ
 * - 削除した GPT-OSS 120B の ID と、そのエイリアス（`gpt-oss` / `gpt-oss-120b`）: 同じ低価格帯の
 *   Haiku 5.5 へ読み替える（サブエージェント定義・ワークスペース設定に保存済みの値を壊さないため）
 * - Sonnet 4.6 の旧形式（`-20250929-v1:0` サフィックス付き）: AWS が
 *   新形式の簡略 ID のみ受け付けるよう切り替えたため、旧形式は
 *   InvokeModel でエラーになる
 */
export const LEGACY_BEDROCK_ID_MAP = {
    'jp.anthropic.claude-sonnet-4-6': BEDROCK_GLOBAL_SONNET_5_5_ID,
    'jp.anthropic.claude-haiku-4-5-20251001-v1:0': BEDROCK_GLOBAL_HAIKU_5_5_ID,
    'global.anthropic.claude-haiku-4-5-20251001-v1:0': BEDROCK_GLOBAL_HAIKU_5_5_ID,
    'global.anthropic.claude-sonnet-4-6-20250929-v1:0': BEDROCK_GLOBAL_SONNET_5_5_ID,
    'global.anthropic.claude-sonnet-4-6': BEDROCK_GLOBAL_SONNET_5_5_ID,
    'global.anthropic.claude-sonnet-5': BEDROCK_GLOBAL_SONNET_5_5_ID,
    'global.anthropic.claude-opus-4-7': BEDROCK_GLOBAL_OPUS_5_5_ID,
    'global.anthropic.claude-opus-4-8': BEDROCK_GLOBAL_OPUS_5_5_ID,
    'global.anthropic.claude-opus-5': BEDROCK_GLOBAL_OPUS_5_5_ID,
    'openai.gpt-oss-120b-1:0': BEDROCK_GLOBAL_HAIKU_5_5_ID,
    'gpt-oss': BEDROCK_GLOBAL_HAIKU_5_5_ID,
    'gpt-oss-120b': BEDROCK_GLOBAL_HAIKU_5_5_ID,
};
/**
 * Bedrock モデル ID を Global プロファイル ID に正規化する
 *
 * - マッピング辞書に存在する JP ID → 対応する Global ID を返す
 * - `jp.anthropic.` で始まる未知 ID → Bedrock デフォルト（Claude Haiku 5.5）にフォールバックし警告を出す
 * - それ以外（既に Global / 他プロバイダー / 非 Claude Bedrock 等）→ 入力をそのまま返す
 *
 * 純粋関数。副作用は未知 JP ID 検出時の `console.warn` のみ。
 */
export function resolveLegacyBedrockModelId(modelId) {
    const mapped = LEGACY_BEDROCK_ID_MAP[modelId];
    if (mapped) {
        return mapped;
    }
    if (modelId.startsWith('jp.anthropic.')) {
        const fallback = DEFAULT_MODELS.bedrock;
        console.warn(`[model-registry] Unknown legacy bedrock model ID "${modelId}" detected; falling back to default "${fallback}".`);
        return fallback;
    }
    return modelId;
}
// プロバイダー別のモデル一覧を取得
export function getModelsForProvider(provider) {
    return MODEL_REGISTRY[provider] ?? [];
}
// モデルIDからモデル定義を取得
// providerを指定すると、そのプロバイダー内で検索（同じモデルIDが複数プロバイダーにある場合に必要）
export function getModelById(modelId, provider) {
    if (provider) {
        const models = MODEL_REGISTRY[provider];
        if (models) {
            return models.find((m) => m.id === modelId);
        }
        return undefined;
    }
    // providerが指定されていない場合は全プロバイダーから検索
    for (const models of Object.values(MODEL_REGISTRY)) {
        const model = models.find((m) => m.id === modelId);
        if (model) {
            return model;
        }
    }
    return undefined;
}
// 全モデルを取得
export function getAllModels() {
    return Object.values(MODEL_REGISTRY).flat();
}
// プロバイダーのデフォルトモデルIDを取得
export function getDefaultModelId(provider) {
    return DEFAULT_MODELS[provider];
}
// プロバイダーのデフォルトモデルを取得
export function getDefaultModel(provider) {
    const defaultModelId = DEFAULT_MODELS[provider];
    const model = getModelById(defaultModelId);
    if (!model) {
        throw new Error(`Default model not found for provider: ${provider}`);
    }
    return model;
}
/**
 * 安定エイリアスの集合（設定にはこの値を保存し、実行時に具体的モデルIDへ解決する）。
 * 並び順は UI 提示順（上位ティアから）。
 */
export const MODEL_ALIASES = [
    'opus',
    'sonnet',
    'haiku',
    'sol',
    'luna',
    'grok',
    'kimi',
];
/**
 * エイリアス（ティア）→ 具体的モデルID のプロバイダー別対応表（単一の真実の情報源）。
 *
 * モデルのバージョンアップ時はこの表だけを更新すれば、保存済みのエイリアス設定
 * （ワークスペース preferredModel・サブエージェント model・Advisor 等）が
 * 新しいモデルへ追従する（保存値の書き換え・マイグレーション不要）。
 *
 * - 各値は当該プロバイダーの MODEL_REGISTRY に存在すること（整合性テストで担保）
 * - 対応表に無いエイリアス指定は既定へフォールバックする
 */
export const MODEL_ID_BY_TIER = {
    bedrock: {
        opus: BEDROCK_GLOBAL_OPUS_5_5_ID,
        sonnet: BEDROCK_GLOBAL_SONNET_5_5_ID,
        haiku: BEDROCK_GLOBAL_HAIKU_5_5_ID,
        sol: BEDROCK_GLOBAL_GPT_6_1_SOL_ID,
        luna: BEDROCK_GLOBAL_GPT_6_LUNA_ID,
        grok: BEDROCK_GLOBAL_GROK_4_7_ID,
        kimi: BEDROCK_GLOBAL_KIMI_K3_ID,
    },
};
/** 安定エイリアスの集合を返す */
export function getModelAliases() {
    return MODEL_ALIASES;
}
/** 値が安定エイリアス（MODEL_ALIASES のいずれか）かを判定する */
export function isModelAlias(value) {
    return MODEL_ALIASES.includes(value);
}
/**
 * エイリアスまたは具体的モデルID を、実行時プロバイダーの具体的モデルIDへ解決する。
 *
 * 解決順（throw しない）:
 * 1. エイリアス → 当該プロバイダーの対応表（MODEL_ID_BY_TIER）
 * 2. 既知の具体的モデルID → 素通し（Bedrock 旧 ID は Global ID へ正規化）
 * 3. 不明 → プロバイダー既定へフォールバックし警告を記録
 *
 * ログには入力値そのものを含めず、プロバイダーと分類のみを残す
 * （内部モデルID詳細・機密の過剰露出を避ける）。
 */
export function resolveModelId(valueOrAlias, provider) {
    // 1. エイリアス優先
    if (isModelAlias(valueOrAlias)) {
        const concreteId = MODEL_ID_BY_TIER[provider]?.[valueOrAlias];
        if (concreteId) {
            return concreteId;
        }
        const fallback = getDefaultModelId(provider);
        console.warn(`[model-registry] Model alias "${valueOrAlias}" is not supported by provider "${provider}"; falling back to provider default.`);
        return fallback;
    }
    // 2. 既知の具体的モデルID の素通し（Bedrock 旧 ID の正規化を含む）
    const normalized = resolveLegacyBedrockModelId(valueOrAlias);
    if (getModelById(normalized, provider)) {
        return normalized;
    }
    // 3. 不明 → プロバイダー既定へ安全側フォールバック
    const fallback = getDefaultModelId(provider);
    console.warn(`[model-registry] Unknown model identifier for provider "${provider}"; falling back to provider default.`);
    return fallback;
}
/**
 * 具体的モデルID から対応エイリアスを逆引きする（UI の整合表示用）。
 * エイリアス自身・Bedrock 旧 ID も対応エイリアスへ解決する。対応が無ければ undefined。
 */
export function getAliasForModelId(modelId, provider) {
    if (isModelAlias(modelId)) {
        return modelId;
    }
    const normalized = resolveLegacyBedrockModelId(modelId);
    const tiers = MODEL_ID_BY_TIER[provider];
    for (const alias of MODEL_ALIASES) {
        if (tiers?.[alias] === normalized) {
            return alias;
        }
    }
    return undefined;
}
/**
 * UI 用のエイリアス選択肢（エイリアス + 解決先モデルの表示名）を返す。
 * ティア非対応プロバイダーには空配列を返す。
 */
export function getModelAliasOptions(provider) {
    const options = [];
    for (const alias of MODEL_ALIASES) {
        const concreteId = MODEL_ID_BY_TIER[provider]?.[alias];
        if (!concreteId)
            continue;
        const def = getModelById(concreteId, provider);
        if (!def)
            continue;
        options.push({ alias, displayName: def.displayName });
    }
    return options;
}
/**
 * AI が申告した ModelSelection を検証し、保存用の正準表現へ正規化する。
 *
 * 受理する modelId（それ以外は undefined ＝却下）:
 * - 安定エイリアス（MODEL_ALIASES のいずれか。当該プロバイダーが対応する場合のみ）
 * - MODEL_REGISTRY に実在する具体的モデルID（Bedrock 旧 ID は既知のもののみ現行 Global ID へ読み替え）
 *
 * 正規化規則は UI の保存規則（model-select-options.ts）と同一:
 * ティア対象のモデルはエイリアス表現へ寄せて保存し、モデルのバージョンアップに追従させる。
 * ティア非対象の具体的モデルは正規化済み ID をそのまま保存する。
 *
 * resolveModelId と異なり、不明な値をプロバイダー既定へフォールバックしない
 * （検証ゲート用途のため、意図しないモデルへの置き換えを避けて明示的に却下する）。
 */
export function normalizeModelSelection(selection) {
    const { provider, modelId } = selection;
    if (!MODEL_REGISTRY[provider]) {
        return undefined;
    }
    if (isModelAlias(modelId)) {
        return MODEL_ID_BY_TIER[provider]?.[modelId] ? { provider, modelId } : undefined;
    }
    // 未知の jp.anthropic.* を既定へフォールバックさせないため、既知マッピングのみ読み替える
    const normalized = LEGACY_BEDROCK_ID_MAP[modelId] ?? modelId;
    if (!getModelById(normalized, provider)) {
        return undefined;
    }
    return { provider, modelId: getAliasForModelId(normalized, provider) ?? normalized };
}
/** 選択可能モデル一覧の提示順 */
const SELECTABLE_MODEL_PROVIDER_ORDER = ['bedrock'];
/**
 * 選択可能モデル一覧を返す（AI へのモデル一覧提示用）。
 * UI の選択肢構築（buildModelOptionGroups）と同じ方針で、ティア対象モデルは
 * エイリアス選択肢で代表し、ティア非対象の具体的モデルのみ ID で列挙する。
 */
export function getSelectableModelOptions() {
    const options = [];
    for (const provider of SELECTABLE_MODEL_PROVIDER_ORDER) {
        for (const { alias, displayName } of getModelAliasOptions(provider)) {
            options.push({ provider, modelId: alias, displayName, isAlias: true });
        }
        for (const model of MODEL_REGISTRY[provider]) {
            if (getAliasForModelId(model.id, provider) !== undefined)
                continue;
            options.push({ provider, modelId: model.id, displayName: model.displayName, isAlias: false });
        }
    }
    return options;
}
/**
 * モデルのティア別名（例: 'sonnet'）を、Bedrock の具体的な ModelSelection へ解決する。
 * ワークスペースの preferredModel など「確実に適用したい既定選択」の指定に用いる。
 * （resolveModelId への薄いラッパー。対応表の実体は MODEL_ID_BY_TIER）
 */
export function getBedrockModelSelection(tier) {
    return { provider: 'bedrock', modelId: resolveModelId(tier, 'bedrock') };
}
//# sourceMappingURL=model-registry.js.map